import React from "react";
// WebSocket instance
let ws = null;
let reconnectTimer = null;
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const INITIAL_RECONNECT_DELAY = 1000;
const RECONNECT_DECAY = 1.5;

let currentReconnectDelay = INITIAL_RECONNECT_DELAY;
let isIntentionalClose = false;

// Connection status
let isConnected = false;
let connectionError = null;

// Connection status listeners
const connectionListeners = new Set();

// Message handlers
const messageHandlers = new Map();

// Register a message handler
export const registerMessageHandler = (type, handler) => {
  if (!messageHandlers.has(type)) {
    messageHandlers.set(type, new Set());
  }
  messageHandlers.get(type).add(handler);
};

// Unregister a message handler
export const unregisterMessageHandler = (type, handler) => {
  if (messageHandlers.has(type)) {
    if (handler) {
      messageHandlers.get(type).delete(handler);
    } else {
      // If no specific handler is provided, remove all handlers for this type
      messageHandlers.delete(type);
    }
  }
};

// Add connection status listener
export const addConnectionListener = (listener) => {
  connectionListeners.add(listener);
  // Immediately notify with current status
  listener(isConnected, connectionError);
};

// Remove connection status listener
export const removeConnectionListener = (listener) => {
  connectionListeners.delete(listener);
};

// Update connection status
const updateConnectionStatus = (connected, error = null) => {
  isConnected = connected;
  connectionError = error;

  // Notify all listeners
  connectionListeners.forEach((listener) => {
    try {
      listener(connected, error);
    } catch (e) {
      console.error("Error in connection listener:", e);
    }
  });
};

// Initialize WebSocket connection
export const initWebSocket = (token) => {
  if (
    ws &&
    (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  clearTimeout(reconnectTimer);
  isIntentionalClose = false;

  try {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws-only/socket`;
    // const wsUrl = `ws://localhost:3001/ws-only/socket`;
    console.log("Connecting to WebSocket:", wsUrl);

    ws = new WebSocket(wsUrl);

    // Setup ping/pong for connection health check
    let pingInterval;
    const startPing = () => {
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 15000);
    };

    ws.onopen = () => {
      console.log("WebSocket connected");
      updateConnectionStatus(true);
      currentReconnectDelay = INITIAL_RECONNECT_DELAY;
      sendMessage({ type: "auth", token });
      startPing();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket message received:", data.type, data);

        if (messageHandlers.has(data.type)) {
          messageHandlers.get(data.type).forEach((handler) => {
            try {
              handler(data);
            } catch (e) {
              console.error(
                `Error in handler for message type ${data.type}:`,
                e
              );
            }
          });
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      updateConnectionStatus(false, "WebSocket connection error");
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
      clearInterval(pingInterval);
      updateConnectionStatus(false);

      if (!isIntentionalClose) {
        reconnectTimer = setTimeout(() => {
          console.log("Attempting to reconnect...");
          initWebSocket(token);
          currentReconnectDelay = Math.min(
            currentReconnectDelay * RECONNECT_DECAY,
            MAX_RECONNECT_DELAY
          );
        }, currentReconnectDelay);
      }
    };
  } catch (error) {
    console.error("WebSocket initialization error:", error);
    updateConnectionStatus(false, "WebSocket initialization failed");
  }
};

// Send a message through WebSocket
export const sendMessage = (message) => {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.error("WebSocket is not connected");
    return false;
  }

  try {
    console.log("Sending WebSocket message:", message.type);
    ws.send(JSON.stringify(message));
    return true;
  } catch (error) {
    console.error("Error sending WebSocket message:", error);
    return false;
  }
};

// Check interaction status for a video
export const checkInteraction = (videoId) => {
  console.log("Checking interaction for video:", videoId);
  return sendMessage({ type: "checkInteraction", videoId });
};

// Check interaction status for a reel
export const checkReelInteraction = (reelId) => {
  console.log("Checking interaction for reel:", reelId);
  return sendMessage({ type: "checkReelInteraction", reelId });
};

// Close WebSocket connection
export const closeWebSocket = () => {
  console.log("Closing WebSocket connection");
  isIntentionalClose = true;
  clearTimeout(reconnectTimer);
  if (ws) {
    ws.close();
    ws = null;
  }
};

// Subscribe to processing progress updates
export const subscribeToProcessingProgress = (videoId) => {
  return sendMessage({
    type: "subscribeProcessingProgress",
    videoId,
  });
};

// Unsubscribe from processing progress updates
export const unsubscribeFromProcessingProgress = (videoId) => {
  return sendMessage({
    type: "unsubscribeProcessingProgress",
    videoId,
  });
};

// Get current connection status
export const getConnectionStatus = () => {
  return { isConnected, error: connectionError };
};

// Initialize WebSocket on page load if token exists
export const initializeWebSocketIfTokenExists = () => {
  const token = localStorage.getItem("token");
  if (token) {
    initWebSocket(token);
  }
};

// Create a React hook for WebSocket connection status
export const useWebSocketStatus = () => {
  const [status, setStatus] = React.useState({
    isConnected,
    error: connectionError,
  });

  React.useEffect(() => {
    const handleStatusChange = (connected, error) => {
      setStatus({ isConnected: connected, error });
    };

    addConnectionListener(handleStatusChange);
    return () => removeConnectionListener(handleStatusChange);
  }, []);

  return status;
};
